import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Modal, Form, Spinner } from 'react-bootstrap';
import {
  FaCheckCircle, FaExclamationCircle, FaBan,
  FaEdit, FaSave, FaTimes, FaMapMarkerAlt, FaFileInvoice
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../services/api';

import { PageHeader }      from '../../components/shared/PageHeader';
import { KpiCards }        from '../../components/shared/KpiCard';
import { SearchFilterBar } from '../../components/shared/SearchFilterBar';
import { DataTable }       from '../../components/shared/DataTable';
import { StatusSwitch }    from '../../components/shared/StatusSwitch';

// ── Filtros ──
const FILTERS = [
  { value: 'ALL',       label: 'Todos',         variant: 'dark'      },
  { value: 'ACTIVE',    label: 'Ativos',        variant: 'success'   },
  { value: 'DEFAULTER', label: 'Inadimplentes', variant: 'danger'    },
  { value: 'INACTIVE',  label: 'Inativos',      variant: 'secondary' },
];

const COLUMNS = ['CNPJ/CPF', 'Nome', 'Telefone', 'Cidade/Estado', 'Status', 'Ações'];

// ==========================================
export default function Clientes() {
  const [clientes,       setClientes]       = useState([]);
  const [searchTerm,     setSearchTerm]     = useState('');
  const [statusFilter,   setStatusFilter]   = useState('ALL');
  const [showModal,      setShowModal]      = useState(false);
  const [currentCliente, setCurrentCliente] = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [loadingEdit,    setLoadingEdit]    = useState(null);

  // ── Fetch lista ──
  const fetchClientes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/customers');
      setClientes(response.data);
    } catch {
      toast.error('Erro ao carregar lista de clientes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClientes(); }, []);

  // ── Processamento ──
  const clientesProcessados = clientes.map(c => ({
    ...c,
    statusGeral: c.financialStatus,
  }));

  const filteredClientes = clientesProcessados.filter(c => {
    const s = searchTerm.toLowerCase();
    const matchText =
      (c.name?.toLowerCase()  ?? '').includes(s) ||
      (c.cnpj                 ?? '').includes(s) ||
      (c.email?.toLowerCase() ?? '').includes(s) ||
      (c.city?.toLowerCase()  ?? '').includes(s);
    const matchStatus = statusFilter === 'ALL' || c.statusGeral === statusFilter;
    return matchText && matchStatus;
  });

  // ── Handlers ──
  const handleNovoCliente = () => {
    setCurrentCliente(null);
    setShowModal(true);
  };

  const handleEditar = async (cliente) => {
    try {
      setLoadingEdit(cliente.id);
      const res = await api.get(`/customers/${cliente.id}`);
      setCurrentCliente(res.data);
      setShowModal(true);
    } catch {
      toast.error('Erro ao carregar dados do cliente.');
    } finally {
      setLoadingEdit(null);
    }
  };

  const handleSave = async (data) => {
    try {
      const payload = {
        name:         data.name,
        cnpj:         data.cnpj.replace(/[^\d]+/g, ''),
        email:        data.email,
        phone:        data.phone,
        isActive:     data.isActive,
        issueInvoice: data.issueInvoice, // 🆕
        street:       data.street,
        number:       data.number,
        complement:   data.complement,
        neighborhood: data.neighborhood,
        zipCode:      data.zipCode,
        city:         data.city,
        state:        data.state,
        ibgeCode:     data.ibgeCode,
      };
      if (data.id) {
        await api.put(`/customers/${data.id}`, payload);
        toast.success('Cliente atualizado com sucesso!');
      } else {
        await api.post('/customers', payload);
        toast.success('Cliente criado com sucesso!');
      }
      setShowModal(false);
      setCurrentCliente(null);
      fetchClientes();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar cliente.');
    }
  };

  const handleInativar = async (id) => {
    if (!window.confirm('Tem certeza que deseja inativar este cliente?')) return;
    try {
      await api.put(`/customers/${id}`, { isActive: false });
      toast.success('Cliente inativado!');
      fetchClientes();
    } catch {
      toast.error('Erro ao inativar cliente.');
    }
  };

  // ── KPI ──
  const kpiItems = [
    { label: 'Total de Clientes',      value: clientesProcessados.length,                                           color: 'primary'   },
    { label: 'Clientes Ativos',        value: clientesProcessados.filter(c => c.statusGeral === 'ACTIVE').length,    color: 'success'   },
    { label: 'Clientes Inativos',      value: clientesProcessados.filter(c => c.statusGeral === 'INACTIVE').length,  color: 'secondary' },
    { label: 'Clientes Inadimplentes', value: clientesProcessados.filter(c => c.statusGeral === 'DEFAULTER').length, color: 'danger', textColor: 'danger' },
  ];

  return (
    <Container fluid className="p-4">

      <PageHeader
        title="Gerenciamento de Clientes"
        buttonLabel="Novo Cliente"
        onButtonClick={handleNovoCliente}
      />

      <Row className="mb-4">
        <KpiCards items={kpiItems} colSize={3} />
      </Row>

      <SearchFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Buscar por nome, CNPJ, email ou cidade..."
        filters={FILTERS}
        activeFilter={statusFilter}
        onFilterChange={setStatusFilter}
      />

      <DataTable columns={COLUMNS} loading={loading} emptyMessage="Nenhum cliente encontrado.">
        {filteredClientes.length > 0 && filteredClientes.map(c => (
          <tr key={c.id}>
            <td className="text-muted">{c.cnpj || '-'}</td>
            <td>{c.name}</td>
            <td>{c.phone || '-'}</td>
            <td>
              {c.city && c.state ? `${c.city}/${c.state}` : c.city || c.state || '-'}
            </td>
            <td>
              {c.statusGeral === 'ACTIVE'    && <span className="text-success fw-bold"><FaCheckCircle className="me-1" />Ativo</span>}
              {c.statusGeral === 'INACTIVE'  && <span className="text-secondary fw-bold"><FaBan className="me-1" />Inativo</span>}
              {c.statusGeral === 'DEFAULTER' && <span className="text-danger fw-bold"><FaExclamationCircle className="me-1" />Inadimplente</span>}
            </td>
            <td>
              <Button
                variant="warning"
                size="sm"
                className="me-2"
                disabled={loadingEdit === c.id}
                onClick={() => handleEditar(c)}
              >
                {loadingEdit === c.id ? <Spinner size="sm" animation="border" /> : <FaEdit />}
              </Button>
              <Button
                variant="danger"
                size="sm"
                disabled={!c.isActive}
                onClick={() => handleInativar(c.id)}
              >
                <FaBan />
              </Button>
            </td>
          </tr>
        ))}
      </DataTable>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Body>
          <ClienteForm
            cliente={currentCliente}
            onSave={handleSave}
            onCancel={() => setShowModal(false)}
          />
        </Modal.Body>
      </Modal>

    </Container>
  );
}

// ==========================================
// FORMULÁRIO
// ==========================================
function ClienteForm({ cliente, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    id:           cliente?.id           || null,
    name:         cliente?.name         || '',
    cnpj:         cliente?.cnpj         || '',
    email:        cliente?.email        || '',
    phone:        cliente?.phone        || '',
    isActive:     cliente?.id ? cliente.isActive : true,
    issueInvoice: cliente?.issueInvoice ?? false, // 🆕
    street:       cliente?.street       || '',
    number:       cliente?.number       || '',
    complement:   cliente?.complement   || '',
    neighborhood: cliente?.neighborhood || '',
    zipCode:      cliente?.zipCode      || '',
    city:         cliente?.city         || '',
    state:        cliente?.state        || '',
    ibgeCode:     cliente?.ibgeCode     || '',
  });

  const [loadingCep, setLoadingCep] = useState(false);

  // 🆕 Configurações de NF da empresa
  const [companyInvoiceEnabled, setCompanyInvoiceEnabled] = useState(false);
  const [loadingInvoiceConfig,  setLoadingInvoiceConfig]  = useState(true);

  // 🆕 Carrega configuração de NF da empresa ao abrir o modal
  useEffect(() => {
    const fetchInvoiceConfig = async () => {
      try {
        const res = await api.get('/company/invoice-settings');
        const enabled  = res.data.invoiceEnabled       || false;
        const defaultN = res.data.invoiceDefaultForNew || false;

        setCompanyInvoiceEnabled(enabled);

        // Se for NOVO cliente, aplica o default da empresa
        if (!cliente?.id) {
          setFormData(prev => ({
            ...prev,
            issueInvoice: enabled ? defaultN : false,
          }));
        }
      } catch {
        setCompanyInvoiceEnabled(false);
      } finally {
        setLoadingInvoiceConfig(false);
      }
    };

    fetchInvoiceConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const buscarCEP = async (cep) => {
    const limpo = cep.replace(/\D/g, '');
    if (limpo.length !== 8) return;
    setLoadingCep(true);
    try {
      const res  = await fetch(`https://viacep.com.br/ws/${limpo}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setFormData(prev => ({
          ...prev,
          street:       data.logradouro || prev.street,
          neighborhood: data.bairro     || prev.neighborhood,
          city:         data.localidade || prev.city,
          state:        data.uf         || prev.state,
          ibgeCode:     data.ibge       || prev.ibgeCode,
        }));
      }
    } catch {
      toast.error('Erro ao consultar o CEP.');
    } finally {
      setLoadingCep(false);
    }
  };

  const handleCepChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({ ...prev, zipCode: value }));
    if (value.replace(/\D/g, '').length === 8) buscarCEP(value);
  };

  return (
    <Card className="border-0">
      <Card.Header className="bg-white d-flex justify-content-between align-items-center">
        <h5 className="mb-0">{cliente ? 'Editar Cliente' : 'Novo Cliente'}</h5>
        <StatusSwitch
          value={formData.isActive}
          onChange={(v) => setFormData(prev => ({ ...prev, isActive: v }))}
        />
      </Card.Header>

      <Card.Body>
        <Form onSubmit={(e) => { e.preventDefault(); onSave(formData); }}>

          {/* ── Dados principais ── */}
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Nome/Razão Social *</Form.Label>
                <Form.Control name="name" value={formData.name} onChange={handleChange} required />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>CPF/CNPJ *</Form.Label>
                <Form.Control name="cnpj" value={formData.cnpj} onChange={handleChange} required />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control type="email" name="email" value={formData.email} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Telefone</Form.Label>
                <Form.Control name="phone" value={formData.phone} onChange={handleChange} />
              </Form.Group>
            </Col>
          </Row>

          {/* ── Endereço ── */}
          <hr />
          <h6 className="mb-3 text-muted">
            <FaMapMarkerAlt className="me-2" />
            Endereço
            {loadingCep && <Spinner size="sm" className="ms-2" />}
          </h6>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>CEP</Form.Label>
                <Form.Control name="zipCode" value={formData.zipCode} onChange={handleCepChange} maxLength="9" placeholder="00000-000" />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>UF</Form.Label>
                <Form.Control name="state" value={formData.state} onChange={handleChange} maxLength="2" style={{ textTransform: 'uppercase' }} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Cidade</Form.Label>
                <Form.Control name="city" value={formData.city} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Rua</Form.Label>
                <Form.Control name="street" value={formData.street} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Número</Form.Label>
                <Form.Control name="number" value={formData.number} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Complemento</Form.Label>
                <Form.Control name="complement" value={formData.complement} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Bairro</Form.Label>
                <Form.Control name="neighborhood" value={formData.neighborhood} onChange={handleChange} />
              </Form.Group>
            </Col>
          </Row>

          {/* ── 🆕 Nota Fiscal ──
          <hr />
          <h6 className="mb-3 text-muted">
            <FaFileInvoice className="me-2" />
            Nota Fiscal
          </h6>

          {loadingInvoiceConfig ? (
            <div className="text-muted small mb-3">
              <Spinner size="sm" animation="border" className="me-2" />
              Carregando configurações...
            </div>
          ) : !companyInvoiceEnabled ? (
            <div className="alert alert-warning py-2 small mb-3">
              ⚠️ Sua empresa <strong>não está habilitada</strong> para emissão de Notas Fiscais.
              Para ativar, acesse <strong>Configurações → Notas Fiscais</strong>.
            </div>
          ) : (
            <Form.Check
              type="switch"
              id="issueInvoice-switch"
              className="mb-3"
              label={
                <span>
                  <strong>Emitir Nota Fiscal</strong> para este cliente
                  <small className="d-block text-muted">
                    Quando ativado, ao receber um pagamento será emitida automaticamente uma NFS-e.
                  </small>
                </span>
              }
              checked={formData.issueInvoice}
              onChange={(e) =>
                setFormData(prev => ({ ...prev, issueInvoice: e.target.checked }))
              }
            />
          )} */}

          {/* ── Botões ── */}
          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button variant="secondary" onClick={onCancel}>
              <FaTimes className="me-1" /> Cancelar
            </Button>
            <Button variant="success" type="submit">
              <FaSave className="me-2" /> Salvar
            </Button>
          </div>

        </Form>
      </Card.Body>
    </Card>
  );
}
